// Round V · V1 — shared W13 label helpers.
// AuditTrail.jsx (W13 page) and SearchBar.jsx (global search → سجل التدقيق)
// import these so both surfaces render `action`, `entity` and role cells
// through the exact same translation paths (w13.action.*, w13.entity.*,
// w13.role.*). No pre-baked English strings survive on either side.

import { fwName } from "@/lib/fwNames";

import { nameCodes } from "@/lib/wsNames";   // Batch 15 · N3 (F-117)

function ltr(s) { return `\u2068${s}\u2069`; }

export function humanize(code) {
  if (!code) return "";
  const out = String(code)
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, c => c.toUpperCase());
  return nameCodes(out.replace(/^(W\d+|A900\d) /i, (m) => m.toUpperCase()));   // Batch 15 · N3: a key named by a code reads as the plain name
}

export function actionLabel(t, code) {
  if (!code) return "";
  const k = `w13.action.${code}`;
  const v = t(k);
  return (v && v !== k) ? v : humanize(code);
}

export function entityLabel(t, type) {
  if (!type) return "";
  const k = `w13.entity.${type}`;
  const v = t(k);
  return (v && v !== k) ? v : humanize(type);
}

// L3 · T2 — On Review engagements the ENGAGEMENT TEAM's role label
// becomes "ممارس" / "Practitioner" regardless of the on-file role.
// EQC + platform-admin roles keep their own labels.
const REVIEW_TEAM = new Set([
  "partner", "auditor", "reviewer", "manager", "supervisor",
  "senior manager", "audit manager", "assistant manager", "preparer",
]);

export function roleLabel(t, role, opts = {}) {
  if (!role) return "";
  const key = String(role).toLowerCase();
  if (opts.isReview && REVIEW_TEAM.has(key)) {
    const kp = "w13.role.practitioner";
    const vp = t(kp);
    return (vp && vp !== kp) ? vp : "Practitioner";
  }
  const k = `w13.role.${key}`;
  const v = t(k);
  return (v && v !== k) ? v : role;
}

// V3 — for form / engagement_forms rows, resolve ref + language-aware
// title from the forms library instead of rendering the raw form_id
// slug (e.g. `management_representation_letter`).
// W1 (Round W) — when the resolved title already starts with the ref
// (library entries do:  "202(a) استراتيجية …"), we do NOT prepend the
// ref again so the entity cell reads "نموذج · استراتيجية…" once, not
// "نموذج · 202(a) 202(a) استراتيجية…".
function _startsWithRef(s, ref) {
  if (!s || !ref) return false;
  const norm = String(s).replace(/^[\s\u2066\u2069\u2068]+/, "").toLowerCase();
  return norm.startsWith(String(ref).toLowerCase());
}

// X1 (Round X) — bidi-stripped label check so we can dedupe the ref
// when the entity label ITSELF ends with it. On tcwg_311a_log rows
// the AR label reads "سجل اتصالات ⁦311(a)⁩" — appending "311(a) …"
// after " · " duplicates it. Handled the same way for the W13 page
// and the global-search hit renderer.
function _labelEndsWithRef(label, ref) {
  if (!label || !ref) return false;
  const clean = String(label).replace(/[\u2066\u2068\u2069]/g, "").trim().toLowerCase();
  return clean.endsWith(String(ref).toLowerCase());
}

// X2 (Round X) — category resolver: on Review engagements, look up
// `tcwg.catReview.<slug>` first and fall back to `tcwg.category.<slug>`
// so "auditor_responsibilities" reads "مسؤوليات الممارس" on reviews
// (matches the 311(a) log module — practitioner scope).
export function tcwgCategoryLabel(t, slug, opts = {}) {
  if (!slug) return "";
  const key = String(slug).trim();
  if (opts.isReview) {
    const kr = `tcwg.catReview.${key}`;
    const vr = t(kr);
    if (vr && vr !== kr) return vr;
  }
  const kc = `tcwg.category.${key}`;
  const vc = t(kc);
  return (vc && vc !== kc) ? vc : key;
}

export function formTail(row, formsIndex, lang) {
  const details = row.details || {};
  const candidate = row.entity_id
    || details.form_id || details.form || details.formId
    || row.entity_ref || details.form_ref || "";
  const fdef = formsIndex && candidate ? formsIndex[candidate] : null;
  if (fdef) {
    const ref = fdef.ref_no || fdef.ref || "";
    const title = (lang === "ar" && fdef.title_ar) ? fdef.title_ar : (fdef.title || "");
    if (title && ref && _startsWithRef(title, ref)) return title;
    const tail = [ref, title].filter(Boolean).join(" ");
    if (tail) return tail;
  }
  return row.entity_ref || details.form_ref || details.form_id
      || details.form || "";
}

// Same signature as the old inline helper on AuditTrail.jsx, but with
// an optional { formsIndex, lang, isReview } bag so form/engagement_forms
// rows substitute the library title for the raw form_id (V3), and
// tcwg_311a_log rows translate the stored category slug through
// `tcwg.catReview.*` / `tcwg.category.*` (W3 + X2).
export function resolveEntityLabel(t, row, lookup, opts = {}) {
  const label = entityLabel(t, row.entity_type || "");
  const details = row.details || {};
  // Round 25f · U1 — framework-change rows show readable names, never "full" / "sme" codes.
  if (/_framework_changed$/.test(row.action || "") && (details.from || details.to)) {
    return `${label} · ${ltr(`${fwName(t, details.from, opts.periodStart)} → ${fwName(t, details.to, opts.periodStart)}`)}`;
  }

  if ((row.entity_type === "form" || row.entity_type === "engagement_forms")
      && opts.formsIndex) {
    const tail = formTail(row, opts.formsIndex, opts.lang);
    return tail ? `${label} · ${ltr(tail)}` : label;
  }

  const stored_ref = row.entity_ref
              || details.ref || details.ref_full || details.form_ref
              || details.line_ref || details.sample_ref
              || details.voucher_no || details.ref_no
              || details.fs_code || details.form || "";
  let stored_desc = row.entity_description
               || details.description || details.title || details.name
               || details.counterparty || details.matter
               || details.filename || "";
  // X2 (Round X) — tcwg category slug → language + engagement-type-
  // aware label. Stored row is not modified (R6/R7).
  if (row.entity_type === "tcwg_311a_log" && stored_desc) {
    const [head, ...rest] = String(stored_desc).split(" — ");
    const headOut = tcwgCategoryLabel(t, (head || "").trim(),
                                        { isReview: opts.isReview });
    stored_desc = rest.length ? `${headOut} — ${rest.join(" — ")}` : headOut;
  }
  const live = lookup && row.entity_id
    ? (lookup[`${row.entity_type}:${row.entity_id}`] || null)
    : null;
  if (live === "__deleted__" || (live && live.deleted)) {
    const tail = stored_ref
      ? `${ltr(stored_ref)} ${t("w13.entityDeleted")}`
      : t("w13.entityDeleted");
    return `${label} · ${tail}`;
  }
  const ref = (live && live.ref) || stored_ref;
  const desc = (live && (live.description || live.title || live.name)) || stored_desc;
  // W1 + X1 (Round W/X) — dedupe the ref when either (a) the label
  // itself already ends with the ref (e.g. "سجل اتصالات 311(a)"),
  // or (b) the desc already starts with the ref
  // ("202(a) استراتيجية المراجعة الشاملة"). The stored row is not
  // modified (R6/R7).
  let tail;
  if (ref && _labelEndsWithRef(label, ref)) {
    tail = desc || "";
  } else if (desc && ref && _startsWithRef(desc, ref)) {
    tail = desc;
  } else {
    tail = [ref, desc].filter(Boolean).join(" ");
  }
  return tail ? `${label} · ${ltr(tail)}` : label;
}

// Same as resolveEntityLabel but without the live lookup — used by the
// Partner deletion-snapshot panel where we only have the stored row.
export function entityDisplay(t, row, opts = {}) {
  return resolveEntityLabel(t, row, null, opts);
}
